/**
 * ConfirmDeletion - Landing page for account deletion confirmation
 * v2.31 - 2026-01-31 - Initial implementation
 *
 * When a user clicks the deletion confirmation link in their email,
 * they land here. This page:
 * 1. Validates the deletion token
 * 2. Shows warning about what will be deleted
 * 3. Allows them to confirm the deletion
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import LinkIcon from '@mui/icons-material/Link';
import SettingsIcon from '@mui/icons-material/Settings';
import { settingsService } from '../services/api';

const ConfirmDeletion: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [status, setStatus] = useState<{
    valid: boolean;
    tenantId?: string;
    email?: string;
    expiresAt?: string;
    error?: string;
  } | null>(null);

  // Check for success in URL params
  const urlSuccess = searchParams.get('success');
  const urlError = searchParams.get('error');

  useEffect(() => {
    if (token) {
      checkStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const checkStatus = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const result = await settingsService.validateDeletionToken(token);
      setStatus(result);
    } catch (err: any) {
      setStatus({
        valid: false,
        error: err.userMessage || 'Failed to validate deletion link'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeletion = async () => {
    if (!token || !acknowledged) return;
    setConfirming(true);
    try {
      const result = await settingsService.confirmDeletion(token);
      if (result.success) {
        setConfirmed(true);
      } else {
        setStatus({
          valid: false,
          error: result.error || 'Failed to delete account'
        });
      }
    } catch (err: any) {
      setStatus({
        valid: false,
        error: err.userMessage || 'Failed to delete account'
      });
    } finally {
      setConfirming(false);
    }
  };

  // Deletion completed
  if (confirmed || urlSuccess) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
              Account Deleted
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your account and all associated data have been permanently deleted.
              We're sorry to see you go.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              If you change your mind, you can always create a new account at{' '}
              <a href="https://app.mylojic.com" style={{ color: '#008080' }}>app.mylojic.com</a>
            </Typography>
            <Button
              variant="outlined"
              href="/"
              sx={{ borderColor: 'primary.main', color: 'primary.main' }}
            >
              Return to Home
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Error state from URL
  if (urlError) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
              Deletion Failed
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {urlError === 'expired'
                ? 'This deletion link has expired. Please request a new one from Settings.'
                : urlError === 'already_deleted'
                ? 'This account has already been deleted.'
                : urlError || 'An error occurred. Please try again.'}
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Invalid or expired link
  if (!status?.valid) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
              Invalid Link
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {status?.error || 'This deletion link is invalid or has expired.'}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Please go to Settings and request a new deletion confirmation email.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Valid token - show deletion confirmation
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#003366', py: 2, px: 3 }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
          MyLojic
        </Typography>
      </Box>

      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <WarningIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main', mb: 1 }}>
              Confirm Account Deletion
            </Typography>
            <Typography variant="body1" color="text.secondary">
              This action cannot be undone
            </Typography>
          </Box>

          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Warning: You are about to permanently delete your account.
            </Typography>
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following will be permanently deleted:
          </Typography>

          <List dense sx={{ bgcolor: '#fff5f5', borderRadius: 1, mb: 3 }}>
            <ListItem>
              <ListItemIcon>
                <StorageIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="All your data and analytics"
                secondary="Discovery data, reports, dashboards"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LinkIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="All connected data sources"
                secondary="OAuth tokens and credentials"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PeopleIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Team members and invites"
                secondary="All team access will be revoked"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SettingsIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Account settings and preferences"
                secondary="Billing history will be retained for legal purposes"
              />
            </ListItem>
          </List>

          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                color="error"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                I understand that this action is permanent and all my data will be deleted.
              </Typography>
            }
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              href="/dashboard/config/settings"
              sx={{ py: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleConfirmDeletion}
              disabled={!acknowledged || confirming}
              startIcon={<DeleteForeverIcon />}
              sx={{
                bgcolor: 'error.main',
                py: 1.5,
                '&:hover': { bgcolor: 'error.dark' }
              }}
            >
              {confirming ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Delete My Account'
              )}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            This confirmation link expires on {new Date(status.expiresAt!).toLocaleDateString()}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default ConfirmDeletion;
